import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Circle,
  Loader2,
  Pencil,
  Shapes,
  Sparkles,
  Square,
  Wand2,
} from "lucide-react";
import Button from "../components/ui/Button";
import { SelectField } from "../components/ui/Field";
import { Alert } from "../components/ui/Feedback";
import DownloadMenu from "../components/logo/DownloadMenu";
import { logoApi } from "../api/logoApi";
import { useLogoStudio } from "../app/LogoProvider";
import { slugForFilename } from "../lib/logoExport";

const SHAPE_OPTIONS = [
  { value: "none", label: "Free-form", icon: Shapes },
  { value: "circle", label: "Circle", icon: Circle },
  { value: "square", label: "Square", icon: Square },
];

const STYLE_OPTIONS = [
  { value: "minimalist", label: "Minimalist" },
  { value: "abstract", label: "Abstract" },
  { value: "mascot", label: "Mascot" },
  { value: "geometric", label: "Geometric" },
  { value: "lettermark", label: "Lettermark" },
];

const BACKGROUND_OPTIONS = [
  { value: "transparent", label: "Transparent" },
  { value: "white", label: "White" },
];

const COLOR_PRESETS = [
  "#111827",
  "#7C3AED",
  "#0F7A52",
  "#B42318",
  "#4F46E5",
  "#64748B",
];

const CONCEPT_COUNT = 10;

/** Checkerboard so a transparent-background concept reads as transparent, not as a white square. */
export const TRANSPARENCY_CHECKER_STYLE = {
  backgroundImage:
    "linear-gradient(45deg, #E2E8F0 25%, transparent 25%), linear-gradient(-45deg, #E2E8F0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #E2E8F0 75%), linear-gradient(-45deg, transparent 75%, #E2E8F0 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
};

/**
 * A brand name plus a small set of constraints (color, shape, style,
 * background) go to Gemini's image model ("Nano Banana"), which returns up
 * to ten concepts in parallel, each rendered as a combination mark with
 * that name as text. Each concept can be downloaded (PNG with a real
 * transparent background, or a vectorised SVG) or sent to the live editor
 * for further instruction-based changes.
 *
 * The form and the concepts themselves live in LogoProvider rather than
 * local state, so navigating to the editor and back doesn't lose them.
 */
export default function LogoGeneratorPage() {
  const navigate = useNavigate();
  const { studio, updateStudio } = useLogoStudio();
  const {
    brandName,
    color,
    useColor,
    shape,
    style,
    background,
    logos,
    hasGenerated,
  } = studio;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleGenerate(event) {
    event.preventDefault();
    if (!brandName.trim() || loading) return;

    setLoading(true);
    setError(null);
    updateStudio({ logos: [] });

    try {
      const data = await logoApi.generate({
        brandName: brandName.trim(),
        color: useColor ? color : undefined,
        shape,
        style,
        background,
        count: CONCEPT_COUNT,
      });
      updateStudio({ logos: data.logos || [], hasGenerated: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(logo) {
    navigate("/logo-editor", {
      state: {
        image: logo.dataUrl,
        mimeType: logo.mimeType,
        prompt: brandName.trim(),
      },
    });
  }

  return (
    <div className="mx-auto max-w-[1300px] px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded bg-[linear-gradient(135deg,#8f5eeb,#7a54e5)]">
          <Wand2
            className="h-4 w-4 text-white"
            strokeWidth={2.25}
            aria-hidden="true"
          />
        </span>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-ink-900">
            Generate a logo
          </h1>
          <p className="text-sm text-ink-600">
            Describe the brand, set a few constraints, and get ten concepts to
            choose from.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleGenerate}
        className="mt-6 rounded-soft border border-ink-200 bg-white p-5 shadow-raised"
      >
        <label htmlFor="logo-brand-name" className="field-label">
          Brand name
        </label>
        <input
          id="logo-brand-name"
          type="text"
          required
          autoFocus
          value={brandName}
          onChange={(e) => updateStudio({ brandName: e.target.value })}
          placeholder="e.g. Northbound Coffee"
          maxLength={40}
          className="control"
        />
        <p className="mt-1.5 text-micro text-ink-400">
          Rendered as text in the logo, and used as the concept the icon is
          built around.
        </p>

        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div>
            <span className="field-label">Shape</span>
            <div className="flex gap-1.5">
              {SHAPE_OPTIONS.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => updateStudio({ shape: value })}
                  aria-pressed={shape === value}
                  className={`flex flex-1 flex-col items-center gap-1 rounded border px-2 py-2.5 text-micro font-semibold transition-colors ${
                    shape === value
                      ? "border-[#8f5eeb] bg-[#8f5eeb]-100 text-[#8f5eeb]-600"
                      : "border-ink-200 text-ink-600 hover:border-ink-300"
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <SelectField
            label="Style"
            value={style}
            onChange={(e) => updateStudio({ style: e.target.value })}
            options={STYLE_OPTIONS}
          />

          <SelectField
            label="Background"
            value={background}
            onChange={(e) => updateStudio({ background: e.target.value })}
            options={BACKGROUND_OPTIONS}
          />
        </div>

        <div className="mt-5">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="field-label mb-0">Primary color</span>
            <button
              type="button"
              onClick={() => updateStudio({ useColor: !useColor })}
              className="text-micro font-semibold text-ink-500 hover:text-ink-800"
            >
              {useColor ? "Let AI choose instead" : "Set a color"}
            </button>
          </div>

          {useColor && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="color"
                value={color}
                onChange={(e) => updateStudio({ color: e.target.value })}
                aria-label="Pick a custom color"
                className="h-9 w-9 cursor-pointer rounded border border-ink-200 bg-white p-0.5"
              />
              <span className="font-mono text-xs text-ink-500">{color}</span>
              <span className="mx-1 h-4 w-px bg-ink-200" aria-hidden="true" />
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => updateStudio({ color: preset })}
                  aria-label={`Use ${preset}`}
                  aria-pressed={color === preset}
                  style={{ backgroundColor: preset }}
                  className={`h-6 w-6 rounded-full border-2 transition-transform ${
                    color === preset
                      ? "scale-110 border-ink-900"
                      : "border-white shadow-hairline"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="mt-5">
            <Alert tone="error">{error}</Alert>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-ink-500">
            Generates {CONCEPT_COUNT} concepts in parallel — usually 20-40
            seconds.
          </p>
          <Button
            type="submit"
            variant="seal"
            icon={Wand2}
            loading={loading}
            disabled={!brandName.trim()}
          >
            {loading ? "Generating…" : `Generate ${CONCEPT_COUNT} concepts`}
          </Button>
        </div>
      </form>

      {loading && (
        <div className="mt-8 flex flex-col items-center justify-center gap-3 py-16 text-center">
          <Loader2
            className="h-7 w-7 animate-spin text-ink-500"
            aria-hidden="true"
          />
          <p className="text-sm text-ink-600">
            Sketching {CONCEPT_COUNT} concepts with Gemini…
          </p>
        </div>
      )}

      {!loading && logos.length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-lg font-semibold text-ink-900">
              {logos.length} concept{logos.length === 1 ? "" : "s"}
            </h2>
            {logos.length < CONCEPT_COUNT && (
              <span className="text-xs text-pending">
                {CONCEPT_COUNT - logos.length} concept
                {CONCEPT_COUNT - logos.length === 1 ? "" : "s"} didn't come back
                — generate again for a full set.
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {logos.map((logo) => (
              <div
                key={logo.id}
                className="rounded-soft border border-ink-200 bg-white shadow-raised"
              >
                <div
                  className="flex aspect-square items-center justify-center rounded-t-soft p-3"
                  style={TRANSPARENCY_CHECKER_STYLE}
                >
                  <img
                    src={logo.dataUrl}
                    alt="Generated logo concept"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="grid grid-cols-2 gap-1 border-t border-ink-100 p-1.5">
                  <DownloadMenu
                    dense
                    dataUrl={logo.dataUrl}
                    mimeType={logo.mimeType}
                    filenameBase={`${slugForFilename(brandName)}-${logo.id}`}
                  />
                  <button
                    type="button"
                    onClick={() => handleEdit(logo)}
                    title="Edit with Gemini"
                    className="flex flex-col items-center gap-0.5 rounded bg-ink-900 py-1.5 text-white hover:bg-ink-800"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide">
                      Edit
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && !hasGenerated && (
        <div className="mt-10 flex flex-col items-center gap-2 rounded-soft border border-dashed border-ink-200 py-16 text-center">
          <Sparkles className="h-7 w-7 text-ink-300" aria-hidden="true" />
          <p className="font-display text-lg text-ink-900">
            Nothing generated yet
          </p>
          <p className="max-w-reading text-sm text-ink-600">
            Enter a brand name above and generate {CONCEPT_COUNT} concepts to
            start from.
          </p>
        </div>
      )}
    </div>
  );
}
